/**
 * `/__ui` — the visual-regression fixture page (spec M0-F7 §9 U9/V2 and §10 VF1–VF16).
 *
 * Sixteen fixtures, one `<section data-fixture="VFn">` each, in the order of the §10 table.
 * Playwright reads DOM metrics off the exact-tolerance ones (VF1–VF4, VF12, VF14) and takes
 * clipped screenshots of the pixel ones (VF5–VF11, VF13, VF15, VF16). Everything renders from
 * `@elizmonday/ui` alone, so a token that drifts fails here before it fails on a real screen.
 */
import {
  Avatar,
  BottomActionBar,
  Button,
  Dialog,
  DialogContentContainer,
  EntityIcon,
  IconButton,
  PageEmptyState,
  PageErrorState,
  SidePanel,
  SidebarRow,
  StatusLabel,
  ViewTab,
  Wordmark,
  BrandLogo,
  colorVars,
  colors,
  dimensionVars,
  dimensions,
  icons,
  layerVars,
  layers,
  statusColorHex,
  statusPalette,
  typography,
  typographyVars,
} from '@elizmonday/ui';
import { useState } from 'react';
import {
  GROUP_HEADER_HEIGHT,
  GROUP_BAR_WIDTH,
  ROW_HEIGHT,
  COLUMN_HEADER_ROW_HEIGHT,
} from '../../modules/board-view/table/layout';
import './harness.css';

function Fixture({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section className="ez-fixture" data-fixture={id} data-testid={`fixture-${id}`}>
      <div className="ez-fixture__title">{`${id} · ${title}`}</div>
      {children}
    </section>
  );
}

export function UiGallery() {
  const [panelOpen, setPanelOpen] = useState(true);

  return (
    <div className="ez-gallery-page" data-testid="ui-gallery">
      <h1>Eli Z Monday · UI fixtures</h1>
      <p>
        The sixteen visual-regression fixtures of M0-F7 §10. Every value comes from
        <code> @elizmonday/ui</code>; nothing on this page is hand-written chrome.
      </p>

      <Fixture id="VF1" title="Typography ramp">
        <table className="ez-metrics">
          <tbody>
            {Object.entries(typography).map(([role, spec]) => (
              <tr key={role} data-role={role}>
                <th scope="row">{role}</th>
                <td
                  data-testid={`type-${role}`}
                  style={{ font: `var(${typographyVars[role as keyof typeof typography]})` }}
                >
                  {`${spec.weight} ${spec.size}/${spec.lineHeight} — The quick brown fox`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Fixture>

      <Fixture id="VF2" title="Colour tokens">
        <div className="ez-fixture__grid">
          {Object.entries(colors).map(([name, value]) => (
            <span
              key={name}
              className="ez-swatch"
              data-token={colorVars[name as keyof typeof colors]}
              data-expected={value}
              style={{ backgroundColor: `var(${colorVars[name as keyof typeof colors]})` }}
            >
              {name}
            </span>
          ))}
        </div>
      </Fixture>

      <Fixture id="VF3" title="Status palette (40 × base/hover/selected)">
        <div className="ez-fixture__grid">
          {Object.keys(statusPalette).map((name) => (
            <span key={name} data-status={name} className="ez-fixture__grid">
              {(['base', 'hover', 'selected'] as const).map((state) => (
                <span
                  key={state}
                  className="ez-swatch"
                  data-state={state}
                  data-expected={statusColorHex(name, state)}
                  style={{ backgroundColor: statusColorHex(name, state), width: 40, height: 24 }}
                />
              ))}
              <span style={{ fontSize: 11 }}>{name}</span>
            </span>
          ))}
        </div>
      </Fixture>

      <Fixture id="VF4" title="Spacing / radii / shadow ramp">
        <table className="ez-metrics">
          <tbody>
            {Object.entries(dimensions).map(([name, value]) => (
              <tr key={name}>
                <th scope="row">{dimensionVars[name as keyof typeof dimensions]}</th>
                <td data-testid={`dim-${name}`} data-expected={value}>
                  <span
                    style={{
                      display: 'inline-block',
                      height: 8,
                      width: `var(${dimensionVars[name as keyof typeof dimensions]})`,
                      backgroundColor: 'var(--primary-color)',
                    }}
                  />
                </td>
              </tr>
            ))}
            {Object.entries(layers).map(([name, value]) => (
              <tr key={name}>
                <th scope="row">{layerVars[name as keyof typeof layers]}</th>
                <td data-testid={`layer-${name}`} data-expected={value} />
              </tr>
            ))}
          </tbody>
        </table>
      </Fixture>

      <Fixture id="VF5" title="Button matrix (kind × size × state)">
        <div className="ez-fixture__grid">
          {(['primary', 'secondary', 'tertiary'] as const).map((kind) =>
            (['small', 'medium'] as const).map((size) => (
              <Button key={`${kind}-${size}`} kind={kind} size={size}>
                {`${kind} ${size}`}
              </Button>
            )),
          )}
          <Button kind="primary" disabled>
            disabled
          </Button>
          <IconButton icon={icons.add} ariaLabel="Add" />
        </div>
      </Fixture>

      <Fixture id="VF6" title="Menu (title + sections + dividers + submenu chevrons)">
        <div className="ez-menu" style={{ position: 'relative', width: 264 }}>
          <div className="ez-menu__item">
            <span>Board options</span>
          </div>
          <hr className="ez-menu__divider" />
          <button type="button" className="ez-menu__item">
            <span>Activity log</span>
          </button>
          <button type="button" className="ez-menu__item">
            <span>Move to</span>
            <span aria-hidden="true">▸</span>
          </button>
          <hr className="ez-menu__divider" />
          <button type="button" className="ez-menu__item" data-danger>
            <span>Delete</span>
          </button>
        </div>
      </Fixture>

      <Fixture id="VF7" title="Dialog / modal chrome">
        <DialogContentContainer>
          <div style={{ width: 320, padding: 8 }}>Dialog content container</div>
        </DialogContentContainer>
      </Fixture>

      <Fixture id="VF8" title="Right-docked pane chrome">
        <div style={{ position: 'relative', height: 160, border: '1px solid var(--layout-border-color)' }}>
          <SidePanel open={panelOpen} title="Notifications" onClose={() => setPanelOpen(true)}>
            <p>Pane body</p>
          </SidePanel>
        </div>
      </Fixture>

      <Fixture id="VF9" title="Side panel chrome">
        <div style={{ position: 'relative', height: 160, border: '1px solid var(--layout-border-color)' }}>
          <SidePanel open title="Item card" onClose={() => undefined}>
            <p>Panel body</p>
          </SidePanel>
        </div>
      </Fixture>

      <Fixture id="VF10" title="Sidebar row states">
        <div style={{ width: 279 }}>
          <SidebarRow label="Default" icon={<EntityIcon kind="board" />} />
          <SidebarRow label="Selected" icon={<EntityIcon kind="board" />} selected />
          <SidebarRow label="Nested 1" icon={<EntityIcon kind="folder" />} level={1} />
          <SidebarRow label="Nested 2" icon={<EntityIcon kind="board" />} level={2} />
          <SidebarRow label="Nested 3" icon={<EntityIcon kind="doc" />} level={3} />
        </div>
      </Fixture>

      <Fixture id="VF11" title="View-tab bar">
        <div className="ez-board__tabs" style={{ position: 'relative', paddingLeft: 0 }}>
          <ViewTab label="Main table" selected />
          <ViewTab label="Timeline" />
          <ViewTab label="Table" />
          <button type="button" className="ez-board-tab ez-board-tab--all">
            All
          </button>
          <button type="button" className="ez-board-tab ez-board-tab--add" aria-label="Add view">
            +
          </button>
        </div>
      </Fixture>

      <Fixture id="VF12" title="Grid metrics">
        <table className="ez-metrics">
          <tbody>
            <tr>
              <th scope="row">row height</th>
              <td data-testid="metric-row-height">{ROW_HEIGHT}</td>
            </tr>
            <tr>
              <th scope="row">column header row</th>
              <td data-testid="metric-column-header">{COLUMN_HEADER_ROW_HEIGHT}</td>
            </tr>
            <tr>
              <th scope="row">group header</th>
              <td data-testid="metric-group-header">{GROUP_HEADER_HEIGHT}</td>
            </tr>
            <tr>
              <th scope="row">group colour bar</th>
              <td data-testid="metric-group-bar">{GROUP_BAR_WIDTH}</td>
            </tr>
          </tbody>
        </table>
      </Fixture>

      <Fixture id="VF13" title="Bottom action bar">
        <div style={{ position: 'relative', height: 96 }}>
          <BottomActionBar open summary="3 items selected" />
        </div>
      </Fixture>

      <Fixture id="VF14" title="Empty state + error state">
        <div className="ez-fixture__grid" style={{ alignItems: 'flex-start' }}>
          <div style={{ width: 320 }}>
            <PageEmptyState title="Nothing here yet" hint="Create something to get started." />
          </div>
          <div style={{ width: 320 }}>
            <PageErrorState kind="not-found" />
          </div>
        </div>
      </Fixture>

      <Fixture id="VF15" title="Brand marks">
        <div className="ez-fixture__grid">
          {[20, 28, 40].map((size) => (
            <Wordmark key={size} height={size} />
          ))}
          {([16, 24, 32, 40] as const).map((size) => (
            <BrandLogo key={size} size={size} />
          ))}
        </div>
      </Fixture>

      <Fixture id="VF16" title="Focus ring on every wrapper">
        <div className="ez-fixture__grid">
          <Button>Focusable button</Button>
          <IconButton icon={icons.search} ariaLabel="Search" />
          <Avatar text="RZ" />
          <StatusLabel label="Done" color="done_green" />
          <Dialog open={false}>
            <span />
          </Dialog>
        </div>
      </Fixture>
    </div>
  );
}
